import Button from '@app/components/common/Button';
import DatePicker from '@app/components/common/DatePicker';
import Divider from '@app/components/common/Divider';
import Grid from '@app/components/common/Grid';
import { labelStyles } from '@app/components/common/Input/styles';
import Typography from '@app/components/common/Typography';
import { getDatesByReportPeriod } from '@app/helpers/management/employees/employees';
import {
  DateTime,
  EMPLOYEES_SCREEN_DATE_FORMAT,
  EReportPeriod,
  IEmployeeDetailsScreenFilters,
  IEmployeesScreenFilters,
} from '@westondev/tableturn-core';
import { useContext, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { EmployeesScreenContext } from '../../EmployeesContext';
import { TIME_PERIOD_TEXT } from './constants';

const FULL_WIDTH_BUTTON = {
  width: '100%',
};

const TimePeriodToolTip = ({ t }: WithTranslation) => {
  const {
    filters: mainScreenFilters,
    user,
    setDetailsFilters,
    setFilters,
    detailsFilters,
  } = useContext(EmployeesScreenContext);

  const filters = user ? detailsFilters : mainScreenFilters;

  const startDateUTC = useMemo(() => {
    if (filters.startDate) {
      return DateTime.fromFormat(filters.startDate, 'yyyy/MM/dd').toISODate();
    }

    return '';
  }, [filters.startDate]);

  const endDateUTC = useMemo(() => {
    if (filters.endDate) {
      return DateTime.fromFormat(filters.endDate, 'yyyy/MM/dd').toISODate();
    }

    return '';
  }, [filters.endDate]);

  const handleOnPress = (
    period: EReportPeriod,
    extraInfo?: Partial<IEmployeesScreenFilters>,
  ) => {
    if (filters.period === period) {
      return;
    }
    if (user) {
      return setDetailsFilters({
        period: period,
        ...getDatesByReportPeriod(period),
        ...extraInfo,
      });
    }
    setFilters({
      period: period,
      ...getDatesByReportPeriod(period),
      ...extraInfo,
    });
  };

  const handleOnDateChange = (
    value: string,
    field: keyof IEmployeeDetailsScreenFilters,
  ) => {
    const luxonNewDate = DateTime.fromISO(value, { zone: 'utc' });
    const newDate = luxonNewDate.toFormat('yyyy/MM/dd');

    let filtersObject: Partial<IEmployeesScreenFilters> = {
      [field]: newDate,
    };

    const luxonEndDate = DateTime.fromFormat(
      filters.endDate || '',
      EMPLOYEES_SCREEN_DATE_FORMAT,
    );

    if (
      field === 'startDate' &&
      luxonEndDate.isValid &&
      luxonEndDate < luxonNewDate
    ) {
      filtersObject = {
        ...filtersObject,
        endDate: null,
      };
    }

    if (user) {
      return setDetailsFilters(filtersObject);
    }

    setFilters(filtersObject);
  };

  return (
    <Grid csx={{ width: 400, padding: 10 }} columnGap={10} rowGap={20}>
      <Grid.Item>
        <Typography color="semanticBlue" fontWeight="medium">
          {t('managementModule.employeesScreen.topBar.timePeriod.title')}
        </Typography>
      </Grid.Item>
      <Grid.Item>
        <Grid columnGap={10} rowGap={10}>
          <Grid.Item mb={6}>
            <Button
              onClick={() => handleOnPress(EReportPeriod.TODAY)}
              variant={
                filters.period === EReportPeriod.TODAY ? 'active' : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.TODAY])}
            </Button>
          </Grid.Item>
          <Grid.Item mb={6}>
            <Button
              onClick={() => handleOnPress(EReportPeriod.YESTERDAY)}
              variant={
                filters.period === EReportPeriod.YESTERDAY
                  ? 'active'
                  : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.YESTERDAY])}
            </Button>
          </Grid.Item>
          <Grid.Item mb={6}>
            <Button
              onClick={() => handleOnPress(EReportPeriod.THIS_WEEK)}
              variant={
                filters.period === EReportPeriod.THIS_WEEK
                  ? 'active'
                  : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.THIS_WEEK])}
            </Button>
          </Grid.Item>
          <Grid.Item mb={6}>
            <Button
              onClick={() => handleOnPress(EReportPeriod.THIS_MONTH)}
              variant={
                filters.period === EReportPeriod.THIS_MONTH
                  ? 'active'
                  : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.THIS_MONTH])}
            </Button>
          </Grid.Item>
          <Grid.Item mb={6}>
            <Button
              onClick={() => handleOnPress(EReportPeriod.THIS_YEAR)}
              variant={
                filters.period === EReportPeriod.THIS_YEAR
                  ? 'active'
                  : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.THIS_YEAR])}
            </Button>
          </Grid.Item>
          <Grid.Item mb={6}>
            <Button
              onClick={() =>
                handleOnPress(EReportPeriod.CUSTOM, {
                  startDate: null,
                  endDate: null,
                })
              }
              variant={
                filters.period === EReportPeriod.CUSTOM ? 'active' : 'secondary'
              }
              csx={FULL_WIDTH_BUTTON}>
              {t(TIME_PERIOD_TEXT[EReportPeriod.CUSTOM])}
            </Button>
          </Grid.Item>
        </Grid>
      </Grid.Item>
      {filters.period === EReportPeriod.CUSTOM && (
        <>
          <Grid.Item>
            <Divider />
          </Grid.Item>
          <Grid.Item>
            <Grid columnGap={10}>
              <Grid.Item mb={6}>
                <Typography csx={labelStyles}>
                  {t('viewAllOrdersScreen.modals.filter.start')}
                </Typography>
                <DatePicker
                  required
                  value={startDateUTC}
                  maxValue={DateTime.now().toISODate()}
                  onChange={v => handleOnDateChange(v, 'startDate')}
                />
              </Grid.Item>
              <Grid.Item mb={6}>
                <Typography csx={labelStyles}>
                  {t('viewAllOrdersScreen.modals.filter.end')}
                </Typography>
                <DatePicker
                  required
                  value={endDateUTC}
                  disabled={!startDateUTC}
                  minValue={startDateUTC}
                  maxValue={DateTime.now().toISODate()}
                  onChange={v => handleOnDateChange(v, 'endDate')}
                />
              </Grid.Item>
            </Grid>
          </Grid.Item>
        </>
      )}
    </Grid>
  );
};

export default TimePeriodToolTip;
